<template>
  <section
    :class="{
      SectionBuilderParallaxDeck: true,
    }"
  >
    <GeneralTheme :theme="configuration">
      <div
        :class="{
          container: true,
        }"
      >
        <!-- DeskTop Parallax Deck -->
        <section
          ref="parallaxDeck"
          :class="{
            'black hidden h-screen grid-cols-2 py-18px lg:grid lg:py-120px': true,
          }"
        >
          <div class="relative col-span-2 h-[80vh] overflow-hidden text-wrap lg:col-span-1">
            <div
              v-for="(slide, i) in slides"
              :key="slide._key"
              :ref="(el) => (panelTextEls[i] = el as HTMLElement)"
              :class="{
                'panel-text': true,
              }"
            >
              <h3 class="mb-4 text-40px font-extrabold text-www-color-neutral-medium-dark">
                {{ slide.title }}
              </h3>
              <PortableText
                v-if="slide.description?.homePagePortableText"
                :portable-data="slide.description.homePagePortableText"
              />
            </div>
          </div>
          <div class="p-wrap relative col-span-2 h-[80vh] overflow-hidden lg:col-span-1">
            <div
              v-for="(slide, i) in slides"
              :key="slide._key"
              :ref="(el) => (imageWrapEls[i] = el as HTMLElement)"
              :class="{
                'image-wrap': true,
                [backgroundStyle]: true,
              }"
              :style="`background-image : url(${cdnImage(slide.image?.asset?._ref).asIs});`"
            ></div>
          </div>
        </section>

        <!-- Mobile Parallax Deck -->
        <section class="block lg:hidden">
          <ParallaxDeck
            v-for="slide in slides"
            :key="slide._key"
            v-bind="slide"
          />
        </section>
      </div>
    </GeneralTheme>
  </section>
</template>
<script setup lang="ts">
import type { SectionBuilderParallaxDeck } from '~/sanity/types';
import { onMounted } from 'vue';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { backgroundColorClasses } from '~/components/Atoms/Background/BgColorMap';
import GeneralTheme from '~/components/Molecules/Themes/GeneralTheme.vue';
import PortableText from '~/components/Atoms/PortableText/PortableText.vue';
import ParallaxDeck from './ParallaxDeck.vue';

const props = defineProps<SectionBuilderParallaxDeck>();

gsap.registerPlugin(ScrollTrigger);

const parallaxDeck = ref<HTMLElement | null>(null);
const imageWrapEls = ref<HTMLElement[]>([]);
const panelTextEls = ref<HTMLElement[]>([]);

const backgroundStyle = computed(() => {
  const backgrounds = props.configuration?.background?.background;
  const bgKey =
    Array.isArray(backgrounds) && backgrounds.length > 0 ? backgrounds[0]?.background : undefined;
  return (
    backgroundColorClasses[bgKey as keyof typeof backgroundColorClasses] || 'bg-www-color-white'
  );
});

onMounted(() => {
  const mediaQuery = gsap.matchMedia();

  mediaQuery.add('(min-width: 1024px)', () => {
    gsap.set(imageWrapEls.value, {
      zIndex: (i, _target, targets) => targets.length - i,
    });

    imageWrapEls.value.forEach((image, i) => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: parallaxDeck.value,
          start: () => 'top -' + window.innerHeight * (i + 0.5),
          end: () => '+=' + window.innerHeight,
          scrub: true,
          toggleActions: 'play none reverse none',
          invalidateOnRefresh: true,
        },
      });

      tl.to(image, { height: 0 });
    });

    panelTextEls.value.forEach((text, i) => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: parallaxDeck.value,
          start: () => 'top -' + window.innerHeight * i,
          end: () => '+=' + window.innerHeight,
          scrub: true,
          toggleActions: 'play none reverse none',
          invalidateOnRefresh: true,
        },
      });

      tl.to(text, { duration: 0.33, opacity: 1, y: '50%', zIndex: i + 2 }).to(
        text,
        { duration: 0.33, opacity: 0, y: '0%', zIndex: 1 },
        0.66
      );
    });

    ScrollTrigger.create({
      trigger: parallaxDeck.value,
      scrub: true,
      pin: true,
      start: () => 'top top',
      end: () => '+=' + (imageWrapEls.value.length - 1) * window.innerHeight,
      invalidateOnRefresh: true,
    });
  });
});
</script>

<style lang="scss">
.panel-text {
  position: absolute;
  left: 0%;
  top: 0%;
  right: 0%;
  bottom: 0%;
  z-index: 1;
  width: 100%;
  height: 50%;
  transform: translateY(100%);
  opacity: 0;
}

.image-wrap {
  position: absolute;
  left: 0%;
  top: 0%;
  right: 0%;
  bottom: 0%;
  z-index: 1;
  background-position: 50% 50%;
  background-size: contain;
  background-repeat: no-repeat;
}
</style>
